import { zodResolver } from '@hookform/resolvers/zod';
import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useUser } from '@plato/app-core/hooks/use-user';
import { CrmTaskStatus } from '@plato/data/prisma';
import { AutoSaveIndicator } from '@plato/ui-legacy/components/common/auto-save-indicator';
import { DialogForm } from '@plato/ui-legacy/components/dialog/dialog-form';
import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { ModalForm } from '@plato/ui-legacy/components/form-components/modal-form';
import { Badge } from '@plato/ui-legacy/components/list-views/badge';
import { LoadingSpinner } from '@plato/ui-legacy/components/loading/loading-spinner';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { Label } from '@plato/ui-legacy/components/ui/label';
import { Text } from '@plato/ui-legacy/components/ui/text';
import { useToast } from '@plato/ui-legacy/components/ui/use-toast';
import { useFormAutoSave } from '@plato/ui-legacy/lib/hooks/use-form-auto-save';
import { IconButton } from '@plato/ui/candidates/icon-button';
import {
    DialogBody,
    DialogFooter,
    DialogHeader,
    DialogIconClose,
    DialogTitle,
    DialogTitleGroup,
} from '@plato/ui/containers/dialog';
import {
    ArrowUpRight,
    CalendarIcon,
    CheckIcon,
    ChevronLeftIcon,
    CircleUserRoundIcon,
    Trash2Icon,
    UserIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useLatest } from 'react-use';
import type { CrmTaskDTO } from '../../../server/dto/crm-task-dto.ts';
import { crmTaskInputDtoSchema, type CrmTaskInputDTO } from '../../../server/dto/crm-task-input-dto.ts';
import { SearchEntity, SearchEntityRoute } from '../../../server/types/search-types.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { CommentsThread } from '../../comments/components/comments-thread.tsx';
import { FileUploader } from '../../crm-attachment/components/file-uploader.tsx';
import { RichTextInput } from '../../rich-text/components/rich-text-input.tsx';
import { UsersSelect } from '../../shared/components/form-items-with-fetch/users-select.tsx';
import { useSearchEntityNavigation } from '../../shared/hooks/use-search-entity.ts';
import { useUpdateTask } from '../hooks/use-update-task.ts';
import { DeleteTaskDialog } from './delete-task-dialog.tsx';
import { DueDatePicker, TaskLinksInput } from './task-form-fields.tsx';

type TaskRelatedDetailsLinkProps = {
    links: CrmTaskInputDTO['links'];
};

function TaskRelatedDetailsLink({ links }: TaskRelatedDetailsLinkProps): ReactNode {
    const { t } = useTranslation('tasks');
    const { navigateToEntity } = useSearchEntityNavigation();

    const relatedLink =
        links.find((link) => link.modelType !== (SearchEntity.Customer as string)) ||
        links.find((link) => link.modelType === (SearchEntity.Customer as string));

    if (!relatedLink) {
        return null;
    }

    return (
        <Link
            onClick={(e) => {
                e.preventDefault();

                navigateToEntity({
                    entity: relatedLink.modelType as SearchEntity,
                    erp_id: relatedLink.modelId,
                });

                return null;
            }}
            to={`${SearchEntityRoute[relatedLink.modelType as keyof typeof SearchEntityRoute]}/${relatedLink.modelId}`}
            className="text-sm font-medium text-blue-500 px-3"
        >
            {t('show_details', { ns: 'common' })}
        </Link>
    );
}

type TaskDetailProps = {
    taskId: string;
    onBack?: () => void;
    onClose?: () => void;
};

type TaskFormProps = {
    task: CrmTaskDTO;
    onBack?: () => void;
    onClose?: () => void;
};

function TaskForm({ task, onBack, onClose }: TaskFormProps) {
    const { t } = useTranslation('tasks');
    const { pathname } = useLocation();
    const isCustomerPage = pathname.includes('/customers/');
    const sendEvent = useAnalytics();
    const { toast } = useToast();
    const { enableFileUpload } = useAppConfig();
    const user = useUser();
    const apiUtils = api.useUtils();

    const defaultValues = useMemo(() => {
        return {
            dueDate: task.dueDate ?? new Date(),
            links: task.links,
            description: task.descriptionRich,
            assignedToId: task.assignedTo.id,
            status: task.status,
            attachments: task.attachments,
        } satisfies CrmTaskInputDTO;
    }, [task]);

    const form = useForm<CrmTaskInputDTO>({
        defaultValues,
        resolver: zodResolver(crmTaskInputDtoSchema),
    });

    const updateTaskMutation = useUpdateTask({
        onSuccess(data) {
            void apiUtils.platoUser.getManyCustomers.getListWithFilters.invalidate();
            void apiUtils.platoUser.getManyQuotesOrders.getWithFilters.invalidate();

            // Full state after the save — what changed is derived in PostHog by comparing
            // consecutive events per task.
            if (data.dueDate) {
                sendEvent('task_updated', {
                    task_id: data.taskId,
                    due_date: data.dueDate.toISOString(),
                    assignee_id: data.assignedTo.id,
                    assignor_id: data.assignedBy.id,
                    is_assigned_to_oneself: data.assignedTo.id === data.assignedBy.id,
                    related_entity_type: data.links[0]?.modelType,
                    related_entity_id: data.links[0]?.modelId,
                    changed_by_id: user.id,
                    source: 'task_detail',
                });
            }

            // Handle task completion analytics
            if (typeof data === 'object' && 'status' in data && 'taskId' in data) {
                const taskData = data;

                if (taskData.status === 'COMPLETED' && task.status !== taskData.status) {
                    if (taskData.dueDate) {
                        sendEvent('task_completed', {
                            task_id: taskData.taskId,
                            due_date: taskData.dueDate.toISOString(),
                            assignee_id: taskData.assignedTo.id,
                            assignor_id: taskData.assignedBy.id,
                            is_assigned_to_oneself: taskData.assignedTo.id === taskData.assignedBy.id,
                            related_entity_type: taskData.links[0]?.modelType,
                            related_entity_id: taskData.links[0]?.modelId,
                            source: 'task_detail',
                        });
                    }
                }
            }
        },
        onError(message) {
            toast({
                icon: 'warning',
                title: 'Error',
                description: message || 'An error occurred',
            });
        },
    });

    const {
        control,
        watch,
        formState: { isValid, isDirty, errors: formErrors },
    } = form;

    const formStateRef = useLatest({ isDirty, isValid });

    useFormAutoSave(watch, (values) => {
        if (formStateRef.current.isValid && formStateRef.current.isDirty) {
            updateTaskMutation.mutate({
                ...values,
                taskId: task.taskId,
            });
        }
    });

    return (
        <>
            <DialogHeader>
                {onBack && (
                    <IconButton label={t('back', { ns: 'common' })} onClick={onBack}>
                        <ChevronLeftIcon />
                    </IconButton>
                )}
                <DialogTitleGroup>
                    <DialogTitle>{t('details__title')}</DialogTitle>
                    <Badge color={task.status === CrmTaskStatus.OPEN ? 'yellow' : 'green'}>
                        {t(`status_${task.status}`)}
                    </Badge>
                </DialogTitleGroup>
                <AutoSaveIndicator
                    isUpdating={updateTaskMutation.isPending}
                    isSuccess={updateTaskMutation.isSuccess}
                    isError={updateTaskMutation.isError}
                />
                <IconButton
                    label={t('form__delete', { ns: 'common' })}
                    onClick={() => {
                        void DialogStateManager.show(DeleteTaskDialog, {
                            taskId: task.taskId,
                            source: 'task_detail',
                            onDeleted: () => {
                                void apiUtils.platoUser.query.tasks.getManyTasks.find.invalidate();
                                onClose?.();
                            },
                        });
                    }}
                >
                    <Trash2Icon />
                </IconButton>
                <DialogIconClose label={t('form__close', { ns: 'common' })} />
            </DialogHeader>
            <DialogBody className="flex flex-col gap-container-cozy bg-neutral-50">
                <DialogForm.ItemList>
                    <div className="rounded-lg bg-white">
                        <DialogForm.Item>
                            <Label htmlFor="description" className="sr-only">
                                {t('description')}
                            </Label>
                            <DialogForm.Body>
                                <Controller
                                    control={control}
                                    name="description"
                                    render={({ field }) => (
                                        <RichTextInput
                                            trackingContext={{ source: 'task' }}
                                            placeholder={t('tasks__description_placeholder')}
                                            {...field}
                                        />
                                    )}
                                />
                            </DialogForm.Body>
                            <DialogForm.Error error={formErrors.description} />
                        </DialogForm.Item>
                    </div>
                </DialogForm.ItemList>
                <DialogForm.Group>
                    <DialogForm.Panel>
                        <DialogForm.ItemList subgrid>
                            <DialogForm.Item>
                                <DialogForm.Label icon={ArrowUpRight}>
                                    {t('related_to', {
                                        ns: 'common',
                                    })}
                                </DialogForm.Label>
                                <DialogForm.Body className="flex-col">
                                    <Controller
                                        control={control}
                                        name="links"
                                        render={({ field }) => (
                                            <>
                                                <TaskLinksInput {...field} taskId={task.taskId} />
                                                {!isCustomerPage && (
                                                    <TaskRelatedDetailsLink links={field.value} />
                                                )}
                                            </>
                                        )}
                                    />
                                </DialogForm.Body>
                                <ModalForm.Error errorMessage={formErrors.links} />
                            </DialogForm.Item>

                            <DialogForm.Item>
                                <DialogForm.Label htmlFor="dueDate" icon={CalendarIcon}>
                                    {t('due_date', {
                                        ns: 'common',
                                    })}
                                </DialogForm.Label>
                                <DialogForm.Body>
                                    <Controller
                                        control={control}
                                        name="dueDate"
                                        render={({ field }) => (
                                            <DueDatePicker {...field} taskStatus={task.status} />
                                        )}
                                    />
                                </DialogForm.Body>
                                <DialogForm.Error error={formErrors.dueDate} />
                            </DialogForm.Item>
                            <DialogForm.Item>
                                <DialogForm.Label htmlFor="assignedToId" icon={CircleUserRoundIcon}>
                                    {t('assigned_to', {
                                        ns: 'common',
                                    })}
                                </DialogForm.Label>
                                <DialogForm.Body>
                                    <Controller
                                        control={control}
                                        name="assignedToId"
                                        render={({ field }) => (
                                            <UsersSelect {...field} className="w-full justify-start" />
                                        )}
                                    />
                                </DialogForm.Body>
                                <DialogForm.Error error={formErrors.assignedToId} />
                            </DialogForm.Item>
                            <DialogForm.Item>
                                <DialogForm.Label icon={UserIcon}>
                                    {t('tasks__form__created_by_label')}
                                </DialogForm.Label>
                                <DialogForm.Body className="items-center">
                                    <Text color="neutral-600" className="px-3">
                                        {task.createdBy.firstName} {task.createdBy.lastName}
                                    </Text>
                                </DialogForm.Body>
                            </DialogForm.Item>
                        </DialogForm.ItemList>
                    </DialogForm.Panel>
                    {enableFileUpload && (
                        <DialogForm.Panel>
                            <DialogForm.ItemList subgrid>
                                <Controller
                                    control={control}
                                    name="attachments"
                                    render={({ field }) => (
                                        <FileUploader
                                            {...field}
                                            value={field.value ?? []}
                                            analyticsContext="task"
                                        />
                                    )}
                                />
                            </DialogForm.ItemList>
                        </DialogForm.Panel>
                    )}
                </DialogForm.Group>
                <CommentsThread taskId={task.taskId} />
            </DialogBody>
            <DialogFooter>
                {task.status === CrmTaskStatus.OPEN ? (
                    <Button
                        icon={CheckIcon}
                        variant="primary"
                        isLoading={updateTaskMutation.isPending}
                        onClick={() => {
                            form.setValue('status', CrmTaskStatus.COMPLETED);

                            const values = form.getValues();
                            updateTaskMutation.mutate({
                                ...values,
                                taskId: task.taskId,
                            });
                        }}
                    >
                        {t('form__complete_task')}
                    </Button>
                ) : (
                    <Button
                        variant="subtle"
                        isLoading={updateTaskMutation.isPending}
                        onClick={() => {
                            form.setValue('status', CrmTaskStatus.OPEN);
                            const values = form.getValues();

                            updateTaskMutation.mutate({
                                ...values,
                                taskId: task.taskId,
                            });
                        }}
                    >
                        {t('form__open_task')}
                    </Button>
                )}
            </DialogFooter>
        </>
    );
}

export function TaskDetails({ taskId, onBack, onClose }: TaskDetailProps) {
    const sendEvent = useAnalytics();
    const { t } = useTranslation('tasks');
    const isViewEventSentRef = useRef(false);

    const { data: task, isLoading } = api.platoUser.query.tasks.getManyTasks.find.useQuery({
        taskId: taskId || '',
    });

    useEffect(() => {
        if (task && !isViewEventSentRef.current) {
            isViewEventSentRef.current = true;

            if (task.dueDate) {
                sendEvent('task_viewed', {
                    task_id: task.taskId,
                    due_date: task.dueDate.toISOString(),
                    assignee_id: task.assignedTo.id,
                    assignor_id: task.assignedBy.id,
                    is_assigned_to_oneself: task.assignedTo.id === task.assignedBy.id,
                    related_entity_type: task.links[0]?.modelType,
                    related_entity_id: task.links[0]?.modelId,
                });
            }
        }
    }, [task, sendEvent]);

    if (isLoading) {
        return (
            <div className="w-screen py-20 sm:max-w-2xl xl:max-w-5xl">
                <LoadingSpinner />
            </div>
        );
    }

    if (!task) {
        return (
            <div className="w-screen py-20 sm:max-w-2xl xl:max-w-5xl">
                <div className="text-center">{t('task_not_found')}</div>
            </div>
        );
    }

    // Keyed so switching to another task remounts the form instead of reusing the previous
    // task's defaults.
    return <TaskForm key={task.taskId} task={task} onBack={onBack} onClose={onClose} />;
}
